import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Member } from "@pagemates/shared";
import { api } from "../../lib/api";
import { Spinner } from "../../components/ui";
import { MemberRow, quietDays } from "../../components/ClubHome";
import { BackBar } from "../../components/ui";
import { useClub } from "./ClubLayout";

/*
 * Everyone in the club, for when the Club tab's twelve aren't enough: search,
 * a few filters an admin reaches for (admins, new this month, quiet for two
 * months), three sorts, fifty at a time.
 */
type Filter = "all" | "admins" | "new" | "quiet";
type Sort = "name" | "newest" | "active";
const PAGE = 50;

export function MembersPage() {
  const { clubId, club } = useClub();
  const c = club.data!;
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const members = useQuery({ queryKey: ["members", clubId], queryFn: () => api.members(clubId) });
  const books = useQuery({ queryKey: ["books", clubId], queryFn: () => api.clubBooks(clubId) });
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<Sort>("name");
  const [shown, setShown] = useState(PAGE);
  const isAdmin = c.myRole !== "member";
  const all = members.data ?? [];
  const counts = useMemo(() => ({
    admins: all.filter((m) => m.role !== "member").length,
    new: all.filter((m) => Date.now() - new Date(m.joinedAt).getTime() < 30 * 86_400_000).length,
    quiet: all.filter((m) => quietDays(m) >= 60).length,
  }), [all]);
  const t = q.trim().toLowerCase();
  const list = useMemo(() => {
    const keep = (m: Member) => (!t || m.name.toLowerCase().includes(t))
      && (filter === "all" || (filter === "admins" ? m.role !== "member" : filter === "new" ? Date.now() - new Date(m.joinedAt).getTime() < 30 * 86_400_000 : quietDays(m) >= 60));
    const by: Record<Sort, (a: Member, b: Member) => number> = {
      name: (a, b) => a.name.localeCompare(b.name),
      newest: (a, b) => b.joinedAt.localeCompare(a.joinedAt),
      active: (a, b) => quietDays(a) - quietDays(b),
    };
    return all.filter(keep).sort(by[sort]);
  }, [all, t, filter, sort]);

  const chip = (k: Filter, label: string, n?: number) => (
    <button key={k} type="button" aria-pressed={filter === k} onClick={() => { setFilter(k); setShown(PAGE); }}>{label}{n !== undefined && <span className="num"> {n}</span>}</button>
  );
  return (
    <main className="page stack members-page">
      <BackBar label="Club" link={{ to: "/clubs/$clubId/club", params: { clubId } }} />
      <div className="row spread"><h1>Members</h1><span className="muted num">{all.length}</span></div>
      <input id="members-find" className="input" type="search" placeholder="Find a member" value={q} onChange={(e) => { setQ(e.target.value); setShown(PAGE); }} autoComplete="off" />
      <div className="row spread members-tools">
        <div className="seg" role="group" aria-label="Show">
          {chip("all", "Everyone")}{chip("admins", "Admins", counts.admins)}{chip("new", "New this month", counts.new)}{chip("quiet", "Quiet 60+ days", counts.quiet)}
        </div>
        <label className="row small muted" style={{ gap: 6 }}>Sort
          <select id="members-sort" className="input sm" value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
            <option value="name">Name</option><option value="newest">Newest</option><option value="active">Recently active</option>
          </select>
        </label>
      </div>
      {members.isPending ? <Spinner /> : (
        <section className="members-col">
          {list.length === 0 ? <p className="muted small">Nobody matches.</p> : (
            <div className="mlist">{list.slice(0, shown).map((m) => <MemberRow key={m.id} m={m} club={c} meId={me.data?.id} canManage={isAdmin} isOwner={c.myRole === "owner"} current={books.data?.current ?? null} />)}</div>
          )}
          {list.length > shown && <button type="button" className="btn sm see-all" onClick={() => setShown((n) => n + PAGE)}>Show {Math.min(PAGE, list.length - shown)} more · {list.length - shown} left</button>}
        </section>
      )}
    </main>
  );
}
